import type {FunctionComponent} from 'preact';
import {useCallback, useEffect, useMemo, useState} from 'preact/hooks';

import {useBugsnag} from '~/foundation/Bugsnag/hooks';
import {useRootProvider} from '~/foundation/RootProvider/hooks';
import type {FeatureName} from '~/foundation/RootProvider/types';

import {I18nContext, SUPPORTED_LOCALES} from './context';
import type {Dictionary, Locale} from './types';

function isSupportedLocale(locale: any): locale is Locale {
  return SUPPORTED_LOCALES.includes(locale);
}

const FEATURES_LOADED: FeatureName[] = [];
const TRANSLATIONS: Dictionary = new Map();

export const I18nProvider: FunctionComponent<{
  getFeatureDictionary?: (locale: Locale) => Promise<Dictionary>;
}> = ({children, getFeatureDictionary}) => {
  const {notify} = useBugsnag();
  const [locale, setLocale] = useState<Locale>(
    // eslint-disable-next-line no-process-env
    (process.env.BUILD_LOCALE as Locale) || 'en',
  );
  const {featureName} = useRootProvider();
  const [loading, setLoading] = useState(TRANSLATIONS.size < 1);

  const getDefaultLanguage = useCallback(() => {
    try {
      const shopifyLocale =
        document.documentElement.lang || window.Shopify?.locale;

      if (isSupportedLocale(shopifyLocale)) {
        return shopifyLocale;
      }

      const parsedLocale = new Intl.Locale(shopifyLocale);

      if (parsedLocale.language && isSupportedLocale(parsedLocale.language)) {
        // Fallback on the unsupported language, pick the language without the region (e.g. 'en-GB' -> 'en')
        return parsedLocale.language;
      } else {
        // eslint-disable-next-line no-console
        console.error(`Unsupported locale: "${shopifyLocale}"`);
      }

      for (const preferredLanguage of navigator.languages) {
        if (isSupportedLocale(preferredLanguage)) {
          return preferredLanguage;
        }

        const parsedLocale = new Intl.Locale(preferredLanguage);

        if (parsedLocale.language && isSupportedLocale(parsedLocale.language)) {
          return parsedLocale.language;
        }
      }
    } catch (error) {
      // This usually happens when the Intl API has detected an unsupported locale (e.g. 'mamma mia')
      if (error instanceof Error) {
        notify(error);
      }
    }
    return 'en';
  }, [notify]);

  const loadDictionary = useCallback(async () => {
    if (!isSupportedLocale(locale)) {
      return;
    }

    /**
     * Add the shared translations to the dictionary.
     */
    if (!TRANSLATIONS.has(locale)) {
      /**
       * We utilize a loading state to ensure that the tree doesn't render early and display translation keys instead of
       * translated content.
       */
      setLoading(true);

      const dictionary: Dictionary = await import(
        `./translations/${locale}.json`
      );
      TRANSLATIONS.set(locale, dictionary);
    }

    /**
     * Add the feature translations to the dictionary.
     */
    if (
      featureName &&
      !FEATURES_LOADED.includes(featureName) &&
      getFeatureDictionary
    ) {
      setLoading(true);

      const otherFeaturesTranslations = TRANSLATIONS.get(locale);
      const featureDictionary = getFeatureDictionary
        ? await getFeatureDictionary(locale)
        : {};
      FEATURES_LOADED.push(featureName);
      TRANSLATIONS.set(locale, {
        ...otherFeaturesTranslations,
        ...featureDictionary,
      });
    }

    setLoading(false);
  }, [featureName, getFeatureDictionary, locale]);

  useEffect(() => {
    const defaultLanguage = getDefaultLanguage();
    setLocale(defaultLanguage);
  }, [getDefaultLanguage]);

  useEffect(() => {
    try {
      loadDictionary();
    } catch (error) {
      if (error instanceof Error) {
        notify(error);
      }
    }
  }, [loadDictionary, locale, notify]);

  const value = useMemo(() => {
    return {
      locale,
      translations: TRANSLATIONS,
    };
  }, [locale]);

  return (
    <I18nContext.Provider value={value}>
      {!loading && children}
    </I18nContext.Provider>
  );
};
